import type { FileAttachment, MessageAttachment, UploadedFile } from '~/types/models';

export const MESSAGE_ATTACHMENT_MAX_BYTES = 20 * 1024 * 1024;
export const MESSAGE_ATTACHMENT_MAX_COUNT = 5;

export function messageAttachmentFileScope(clientId: string) {
    return {
        entityType: 'client' as const,
        entityId: clientId,
    };
}

const allowedMimeTypes = new Set([
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'text/plain',
    'text/csv',
]);

const allowedExtensions = new Set([
    'csv', 'doc', 'docx', 'gif', 'heic', 'heif', 'jpeg', 'jpg', 'pdf', 'png',
    'ppt', 'pptx', 'txt', 'webp', 'xls', 'xlsx',
]);

export const MESSAGE_ATTACHMENT_ACCEPT = [
    'image/*',
    '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx', '.txt', '.csv',
].join(',');

export function canSendMessageDraft(content: unknown, attachmentCount: number): boolean {
    return String(content || '').trim().length > 0 || attachmentCount > 0;
}

export function validateMessageAttachment(file: Pick<File, 'name' | 'size' | 'type'>): string | null {
    if (file.size <= 0) return `${file.name} is empty.`;
    if (file.size > MESSAGE_ATTACHMENT_MAX_BYTES) return `${file.name} is larger than 20 MB.`;

    const extension = file.name.split('.').pop()?.toLowerCase() || '';
    const supported = file.type.startsWith('image/') || allowedMimeTypes.has(file.type) || allowedExtensions.has(extension);
    return supported ? null : `${file.name} is not a supported image, PDF, or Office document.`;
}

export function toMessageAttachment(file: UploadedFile): MessageAttachment {
    return toFileAttachment(file);
}

export function toFileAttachment(file: UploadedFile): FileAttachment {
    return {
        fileId: file.id,
        fileName: file.fileName,
        fileURL: file.fileURL,
        storagePath: file.storagePath,
        contentType: file.contentType,
        sizeBytes: file.sizeBytes,
    };
}

export function isStoredMessageAttachmentForClient(
    file: Pick<UploadedFile, 'entityType' | 'entityId' | 'uploadedBy'>,
    clientId: string,
    uploaderId: string,
): boolean {
    const isSupportedScope = file.entityType === 'client' || file.entityType === 'message';
    return isSupportedScope && file.entityId === clientId && file.uploadedBy === uploaderId;
}

export function isImageMessageAttachment(attachment: MessageAttachment): boolean {
    return isImageFileAttachment(attachment);
}

export function isImageFileAttachment(attachment: FileAttachment): boolean {
    return String(attachment.contentType || '').startsWith('image/');
}
